import MaterialIcon from '@components/common/MaterialIcon';
import ButtonT from '@components/tailwind-components/Button/ButtonT';
import { Dialog, DialogPanel } from '@headlessui/react';
import type React from 'react';

type TConfirmDialogButton = {
  label: string;
  styling: React.ComponentProps<typeof ButtonT>['styling'];
  onClick: () => void;
  disabled?: boolean;
};

type Props = {
  isOpen: boolean;
  title: string;
  description: string;
  /** Material icon name shown before the title (e.g. 'warning', 'info', 'delete') */
  icon?: string;
  /** Tailwind text-color class for the icon (e.g. 'text-amber-500', 'text-red-500') */
  iconColor?: string;
  buttons: TConfirmDialogButton[];
  onClose: () => void;
};

/**
 * Generic confirmation dialog that renders via a HeadlessUI portal — safe to use
 * from within another modal without losing that modal's state.
 * Renders buttons as a full-width vertical stack.
 */
export const ConfirmDialog: React.FC<Props> = ({
  isOpen,
  title,
  description,
  icon,
  iconColor = 'text-amber-500',
  buttons,
  onClose,
}) => {
  return (
    <Dialog open={isOpen} onClose={onClose} className='relative z-50'>
      <div className='fixed inset-0 bg-black/30' aria-hidden='true' />
      <div className='fixed inset-0 flex items-center justify-center p-4'>
        <DialogPanel className='flex w-full max-w-md flex-col gap-4 rounded-xl bg-white p-5 shadow-2xl'>
          <div className='flex items-center gap-2'>
            {icon && (
              <MaterialIcon
                icon={icon}
                classes={[iconColor, 'text-base', 'leading-none', 'shrink-0']}
              />
            )}
            <h3 className='text-sm font-semibold text-gray-800'>{title}</h3>
          </div>
          <p className='text-xs leading-relaxed text-gray-500'>{description}</p>
          <div className='flex flex-col gap-2 pt-1'>
            {buttons.map((button) => (
              <ButtonT
                key={button.label}
                type='button'
                styling={button.styling}
                size='xs'
                rounding='md'
                className='w-full justify-center'
                disabled={button.disabled}
                onClick={button.onClick}>
                {button.label}
              </ButtonT>
            ))}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};
